import { TimeZone } from 'types/graphql'

import { getDayjsInTimeZone } from 'src/utils/date'

// GOV-6825: Severity escalation as the AICA reverse trial winds down.
// Thresholds:
//   >= 30 days remaining → 'default' (violet)
//   <  30 days remaining → 'warning' (yellow)
//   <   3 days remaining → 'critical' (red)
//   <= 0 days remaining  → 'expired' (red, distinct copy)
//
// IMPORTANT: Keep these thresholds in sync with the api counterpart at
// `api/src/utils/aicaReverseTrial.ts`. The two helpers exist because the
// monorepo doesn't yet have a shared `packages/*` module that web and api
// can both consume; the duplication is intentional and minimal.
export type ReverseTrialSeverity = 'default' | 'warning' | 'critical' | 'expired'

// Calendar-day diff between today and the trial end date in the org's time
// zone, so a trial that ends "tomorrow" by the local calendar reads as 1
// regardless of the time of day at which `trialEndDate` was set.
//
// On the web side `timeZone` is the GovWell `TimeZone` enum value (e.g.
// 'Eastern') as it comes off the `useMyOrg()` GraphQL fragment;
// `getDayjsInTimeZone` handles the IANA mapping internally.
export const getReverseTrialDaysLeftInTimeZone = (
  trialEndDate: Date | string | null | undefined,
  timeZone: TimeZone | null | undefined
): number | undefined => {
  if (!trialEndDate) {
    return undefined
  }
  const trialEnd = getDayjsInTimeZone(trialEndDate, timeZone).startOf('day')
  const today = getDayjsInTimeZone(new Date(), timeZone).startOf('day')
  return trialEnd.diff(today, 'day')
}

export const getReverseTrialSeverity = (daysLeft: number | undefined): ReverseTrialSeverity => {
  if (daysLeft === undefined) {
    return 'default'
  }
  if (daysLeft <= 0) {
    return 'expired'
  }
  if (daysLeft < 3) {
    return 'critical'
  }
  if (daysLeft < 30) {
    return 'warning'
  }
  return 'default'
}
